import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { User } from '../../domain/types';
import {
  AppRole,
  Permission,
  Resource,
  Action,
  hasPermission,
  getRolePermissions,
  ADMIN_ROLES,
  PARTNER_ROLES,
  SELLER_ROLES
} from '../../domain/rbac';

import { useTheme } from '../ThemeContext';
import { useToast } from '../ToastContext';
import { authService } from '../../domain/services/authService';
import { userRepository } from '../../data/userRepository';
import { logger } from '../../infrastructure/logger';
import { getAuth, getIdTokenResult } from 'firebase/auth';
import { ERROR_MESSAGES } from '../../domain/constants/errors';

export interface AuthContextType {
  // User state
  user: User | null;
  isInitialLoading: boolean;

  // Auth methods
  login: (email?: string, password?: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  loginWithFacebook: () => Promise<boolean>;
  loginWithApple: () => Promise<boolean>;
  register: (data: { name: string; email: string; password: string; phone: string; dni: string }) => Promise<{ success: boolean; user?: User; linkedOrders?: string[]; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  sendVerificationEmail: () => Promise<boolean>;

  // RBAC System
  userRole: AppRole;
  userPermissions: Permission[];
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isPartner: boolean;
  isSeller: boolean;
  hasPermission: (resource: Resource, action: Action) => boolean;
  hasAnyPermission: (permissions: { resource: Resource; action: Action }[]) => boolean;
  canAccessAdmin: boolean;
  canAccessPartner: boolean;
  canManageOrders: boolean;

  // Legacy RBAC (for compatibility)
  viewAsUser: boolean;
  toggleViewAsUser: () => void;

  // Token Claims
  tokenClaims: { role?: string; superadmin?: boolean; partnerId?: string } | null;
  refreshTokenClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [viewAsUser, setViewAsUser] = useState(false);
  const [tokenClaims, setTokenClaims] = useState<{ role?: string; superadmin?: boolean; partnerId?: string } | null>(null);
  const { setTheme } = useTheme();
  const { addToast } = useToast();

  // Token claims refresh
  const refreshTokenClaims = useCallback(async () => {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        const tokenResult = await getIdTokenResult(currentUser, true);
        setTokenClaims({
          role: tokenResult.claims.role as string | undefined,
          superadmin: tokenResult.claims.superadmin as boolean | undefined,
          partnerId: tokenResult.claims.partnerId as string | undefined
        });
        logger.info('Token claims refreshed', { role: tokenResult.claims.role });
      } else {
        setTokenClaims(null);
      }
    } catch (error) {
      logger.error('Error refreshing token claims', error instanceof Error ? error : new Error(String(error)));
      setTokenClaims(null);
    }
  }, []);

  // RBAC Computed Values
  // SECURITY: No email-based auto-provisioning. Roles must be set via Firebase Custom Claims.
  const userRole: AppRole = useMemo(() => {
    if (!user) return 'customer';

    // Check token claims first (production - most secure)
    if (tokenClaims?.superadmin === true || tokenClaims?.role === 'superadmin') {
      return 'superadmin';
    }
    if (tokenClaims?.role === 'admin') {
      return 'admin';
    }
    if (tokenClaims?.role && ADMIN_ROLES.includes(tokenClaims.role as AppRole)) {
      return tokenClaims.role as AppRole;
    }

    // Check user document as fallback
    if (user.role === 'superadmin') return 'superadmin';
    if (user.role === 'admin') return 'admin';
    if (user.isAdmin) return 'admin';

    // Check partner
    if (user.partnerId || tokenClaims?.partnerId) return 'partner_owner';

    return 'customer';
  }, [user, tokenClaims]);

  const userPermissions = useMemo(() => getRolePermissions(userRole), [userRole]);
  const isSuperAdmin = useMemo(() => userRole === 'superadmin', [userRole]);
  const isAdmin = useMemo(() => ADMIN_ROLES.includes(userRole), [userRole]);
  const isPartner = useMemo(() => PARTNER_ROLES.includes(userRole), [userRole]);
  const isSeller = useMemo(() => SELLER_ROLES.includes(userRole), [userRole]);
  const canAccessAdmin = useMemo(() => (isAdmin || isSuperAdmin) && !viewAsUser, [isAdmin, isSuperAdmin, viewAsUser]);
  const canAccessPartner = useMemo(() => (isPartner || isSeller || isSuperAdmin) && !viewAsUser, [isPartner, isSeller, isSuperAdmin, viewAsUser]);
  const canManageOrders = useMemo(() => isSuperAdmin || tokenClaims?.role === 'admin' || isPartner, [isSuperAdmin, tokenClaims, isPartner]);

  const checkPermission = useCallback((resource: Resource, action: Action): boolean => {
    return hasPermission(userRole, resource, action);
  }, [userRole]);

  const checkAnyPermission = useCallback((permissions: { resource: Resource; action: Action }[]): boolean => {
    return permissions.some(({ resource, action }) => hasPermission(userRole, resource, action));
  }, [userRole]);

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      setIsInitialLoading(true);
      try {
        const storedUser = await authService.checkSession();
        if (isMounted) {
          if (storedUser) {
            setUser(storedUser);
            if (storedUser.themePreference) setTheme(storedUser.themePreference);
            await refreshTokenClaims();
          }
        }
      } catch (error) {
        logger.error("Auth initialization failed", error instanceof Error ? error : new Error(String(error)));
      } finally {
        if (isMounted) {
          setIsInitialLoading(false);
        }
      }
    };

    initAuth();
    return () => { isMounted = false; };
  }, [setTheme, refreshTokenClaims]);

  // Auth methods
  const login = useCallback(async (email?: string, password?: string): Promise<boolean> => {
    try {
      const result = await authService.login(email, password);
      if (result.success && result.user) {
        setUser(result.user);
        if (result.user.themePreference) setTheme(result.user.themePreference);
        await refreshTokenClaims();
        addToast('¡Bienvenido de vuelta!', 'success');
        return true;
      }
      return false;
    } catch {
      addToast(ERROR_MESSAGES.AUTH_LOGIN_FAILED, 'error');
      return false;
    }
  }, [setTheme, refreshTokenClaims, addToast]);

  const loginWithGoogle = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.loginWithGoogle();
      if (result.success && result.user) {
        setUser(result.user);
        if (result.user.themePreference) setTheme(result.user.themePreference);
        await refreshTokenClaims();
        addToast('¡Bienvenido!', 'success');
        return true;
      }
      return false;
    } catch {
      addToast(ERROR_MESSAGES.AUTH_GOOGLE_LOGIN_FAILED, 'error');
      return false;
    }
  }, [setTheme, refreshTokenClaims, addToast]);

  const loginWithFacebook = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.loginWithFacebook();
      if (result.success && result.user) {
        setUser(result.user);
        if (result.user.themePreference) setTheme(result.user.themePreference);
        await refreshTokenClaims();
        addToast('¡Bienvenido!', 'success');
        return true;
      }
      if (result.error) {
        addToast(result.error, 'error');
      }
      return false;
    } catch {
      addToast(ERROR_MESSAGES.AUTH_LOGIN_FAILED, 'error');
      return false;
    }
  }, [setTheme, refreshTokenClaims, addToast]);

  const loginWithApple = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.loginWithApple();
      if (result.success && result.user) {
        setUser(result.user);
        if (result.user.themePreference) setTheme(result.user.themePreference);
        await refreshTokenClaims();
        addToast('¡Bienvenido!', 'success');
        return true;
      }
      if (result.error) {
        addToast(result.error, 'error');
      }
      return false;
    } catch {
      addToast(ERROR_MESSAGES.AUTH_LOGIN_FAILED, 'error');
      return false;
    }
  }, [setTheme, refreshTokenClaims, addToast]);

  const register = useCallback(async (data: { name: string; email: string; password: string; phone: string; dni: string }) => {
    try {
      const result = await authService.register(data);
      if (result.success && result.user) {
        setUser(result.user);
        if (result.user.themePreference) setTheme(result.user.themePreference);
      }
      return result;
    } catch {
      addToast(ERROR_MESSAGES.AUTH_REGISTER_FAILED, 'error');
      return { success: false, error: 'Registration failed' };
    }
  }, [setTheme, addToast]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setTokenClaims(null);
    setTheme('dark');
    setViewAsUser(false);
    addToast('Sesión cerrada', 'info');
  }, [setTheme, addToast]);

  const refreshUser = useCallback(async () => {
    try {
      const refreshedUser = await authService.checkSession();
      if (refreshedUser) {
        setUser(refreshedUser);
      }
      await refreshTokenClaims();
    } catch (error) {
      logger.error('Error refreshing user', error instanceof Error ? error : new Error(String(error)));
    }
  }, [refreshTokenClaims]);

  const sendVerificationEmail = useCallback(async (): Promise<boolean> => {
    try {
      return await authService.sendVerificationEmail();
    } catch {
      addToast(ERROR_MESSAGES.AUTH_RESET_PASSWORD_FAILED, 'error');
      return false;
    }
  }, [addToast]);

  const toggleViewAsUser = useCallback(() => setViewAsUser(prev => !prev), []);

  const value = useMemo(() => ({
    user,
    isInitialLoading,
    login,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    register,
    logout,
    refreshUser,
    sendVerificationEmail,
    userRole,
    userPermissions,
    isSuperAdmin,
    isAdmin,
    isPartner,
    isSeller,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    canAccessAdmin,
    canAccessPartner,
    canManageOrders,
    viewAsUser,
    toggleViewAsUser,
    tokenClaims,
    refreshTokenClaims
  }), [
    user,
    isInitialLoading,
    login,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    register,
    logout,
    refreshUser,
    sendVerificationEmail,
    userRole,
    userPermissions,
    isSuperAdmin,
    isAdmin,
    isPartner,
    isSeller,
    checkPermission,
    checkAnyPermission,
    canAccessAdmin,
    canAccessPartner,
    canManageOrders,
    viewAsUser,
    toggleViewAsUser,
    tokenClaims,
    refreshTokenClaims
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export default AuthContext;
